import {Calendar, DatabaseBackup, Ellipsis, Medal, ShieldCheck, Trophy, Users, UserSearch} from "lucide-react"

import {
    Sidebar,
    SidebarContent, SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem, useSidebar,
} from "./ui/sidebar"
import {Link, usePage} from "@inertiajs/react";
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from "./ui/dropdown-menu.tsx";
import {User} from "../../types/user.ts";
import {useState} from "react";
import {League} from "@/../types/league.ts";


export function AppSidebar() {
    const {current_league} = usePage<{ current_league: League }>().props;
    const {current_user: currentUser} = usePage<{ current_user: User }>().props;
    const {is_superadmin: isSuperadmin} = usePage<{ is_superadmin: boolean }>().props;
    const {url} = usePage();
    const {state} = useSidebar();  // Get the sidebar state
    const sidebarOpen = state === "expanded";

    const baseItems = [
        {
            title: "Tournaments",
            url: "/events",
            icon: Trophy,
        },
        {
            title: "Standings",
            url: "/standings",
            icon: Medal,
        },
        {
            title: "Teams",
            url: "/teams",
            icon: Users
        },
        {
            title: "Players",
            url: "/players",
            icon: UserSearch,
        },
        {
            title: "Leagues",
            url: "/leagues",
            icon: DatabaseBackup,
        },
        {
            title: "Calendar",
            url: "/calendar",
            icon: Calendar,
        },
        ...(isSuperadmin ? [{
            title: "Admin",
            url: "/superadmin",
            icon: ShieldCheck,
        }] : []),
    ]

    const [sidebarItems] = useState(baseItems);

    return (
        <Sidebar collapsible="icon">
            <SidebarContent>
                <SidebarGroup>
                    <SidebarGroupLabel className="font-display text-xs uppercase tracking-wide">
                        {current_league?.name}
                    </SidebarGroupLabel>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            {sidebarItems.map((item) => (
                                <SidebarMenuItem key={item.title}>
                                    <SidebarMenuButton asChild isActive={url.startsWith(item.url)}>
                                        <Link href={item.url} preserveState={false} preserveScroll={false}>
                                            <item.icon/>
                                            {item.title}
                                        </Link>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            ))}
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
            </SidebarContent>

            <SidebarFooter className="p-0 list-none">
                <SidebarMenuItem className="border-t p-3">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-3">
                            {currentUser?.avatar_url && (
                                <img
                                    src={currentUser.avatar_url}
                                    alt="User avatar"
                                    className="h-8 w-8 shrink-0 rounded-full"
                                />
                            )}
                            <div className={sidebarOpen ? 'block min-w-0' : 'hidden'}>
                                <span className="block truncate text-sm font-medium">
                                    {currentUser?.first_name} {currentUser?.last_name}
                                </span>
                            </div>
                        </div>
                        <DropdownMenu>
                            <DropdownMenuTrigger aria-label="Account menu"
                                                 className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground">
                                <Ellipsis className="h-4 w-4"/>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                                <Link href='/auth/logout' onClick={() => window.location.href = '/auth/logout'}
                                      as='button' className="w-full h-full">
                                    <DropdownMenuItem className="cursor-pointer">
                                        Logout
                                    </DropdownMenuItem>
                                </Link>
                                <Link href='/profile' as='button' className="w-full h-full">
                                    <DropdownMenuItem className="cursor-pointer">
                                        Profile
                                    </DropdownMenuItem>
                                </Link>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </SidebarMenuItem>
            </SidebarFooter>
        </Sidebar>
    )
}
